import { useState, useEffect } from "react";
import { format, parse, isValid, setMonth, setYear } from "date-fns";
import { CalendarIcon, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

interface TimelineDateOverlayProps {
  open: boolean;
  onClose: () => void;
}

export function TimelineDateOverlay({ open, onClose }: TimelineDateOverlayProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [date, setDate] = useState<Date | undefined>();
  const [calendarMonth, setCalendarMonth] = useState<Date>(new Date());
  const [textValue, setTextValue] = useState("");
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const today = new Date();
  const currentYear = today.getFullYear();
  const years = Array.from({ length: 20 }, (_, i) => currentYear + i);

  // Load current target date
  useEffect(() => {
    if (!open || !user?.id || loaded) return;

    async function load() {
      const { data } = await supabase
        .from("assessment_progress")
        .select("answers")
        .eq("user_id", user!.id)
        .eq("module_id", "owner-readiness-exit-timeline")
        .maybeSingle();

      const answers = data?.answers as Record<string, unknown> | null;
      const stored = answers?.target_exit_date as string | undefined;
      if (stored) {
        const d = new Date(stored);
        if (isValid(d)) {
          setDate(d);
          setCalendarMonth(d);
          setTextValue(format(d, "MM/dd/yyyy"));
        }
      }
      setLoaded(true);
    }

    load();
  }, [open, user?.id, loaded]);

  // Reset on close
  useEffect(() => {
    if (!open) setLoaded(false);
  }, [open]);

  const handleTextChange = (val: string) => {
    setTextValue(val);
    for (const fmt of ["MM/dd/yyyy", "yyyy-MM-dd", "MM-dd-yyyy"]) {
      const parsed = parse(val, fmt, new Date());
      if (isValid(parsed) && parsed >= today) {
        setDate(parsed);
        setCalendarMonth(parsed);
        return;
      }
    }
  };

  const handleCalendarSelect = (d: Date | undefined) => {
    setDate(d);
    if (d) {
      setTextValue(format(d, "MM/dd/yyyy"));
      setPopoverOpen(false);
    }
  };

  const handleSave = async () => {
    if (!date || !user?.id) return;
    setSaving(true);

    try {
      const isoDate = format(date, "yyyy-MM-dd");

      // Update assessment_progress.answers
      const { data: existing } = await supabase
        .from("assessment_progress")
        .select("id, answers")
        .eq("user_id", user.id)
        .eq("module_id", "owner-readiness-exit-timeline")
        .maybeSingle();

      if (existing) {
        const updatedAnswers = { ...(existing.answers as Record<string, unknown>), target_exit_date: isoDate };
        await supabase
          .from("assessment_progress")
          .update({ answers: updatedAnswers, updated_at: new Date().toISOString() })
          .eq("id", existing.id);
      }

      // Invalidate queries so tile refreshes
      queryClient.invalidateQueries({ queryKey: ["a2-target-date"] });
      queryClient.invalidateQueries({ queryKey: ["team-succession-date"] });
      queryClient.invalidateQueries({ queryKey: ["assessment-progress", "owner-readiness-exit-timeline"] });

      toast({ title: "Target date updated", description: `Set to ${format(date, "MMMM yyyy")}` });
      onClose();
    } catch (error) {
      toast({ title: "Error saving date", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-xl shadow-lg w-full max-w-md p-6 space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-foreground">Update Target Exit Date</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">Target transition date</label>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Input
                type="text"
                placeholder="MM/DD/YYYY"
                value={textValue}
                onChange={(e) => handleTextChange(e.target.value)}
                className="min-h-[48px] pr-10"
              />
              <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-md text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <CalendarIcon className="h-4 w-4" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <div className="p-3 space-y-3">
                    <div className="flex items-center gap-2 justify-between">
                      <Select
                        value={calendarMonth.getMonth().toString()}
                        onValueChange={(v) => setCalendarMonth(setMonth(calendarMonth, parseInt(v)))}
                      >
                        <SelectTrigger className="h-8 w-[130px] text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {MONTHS.map((m, i) => (
                            <SelectItem key={i} value={i.toString()}>{m}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select
                        value={calendarMonth.getFullYear().toString()}
                        onValueChange={(v) => setCalendarMonth(setYear(calendarMonth, parseInt(v)))}
                      >
                        <SelectTrigger className="h-8 w-[90px] text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {years.map((y) => (
                            <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={handleCalendarSelect}
                      month={calendarMonth}
                      onMonthChange={setCalendarMonth}
                      disabled={(d) => d < today}
                      initialFocus
                      className={cn("p-0 pointer-events-auto")}
                    />
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </div>
          {textValue && !date && (
            <p className="text-xs text-muted-foreground">Enter a future date as MM/DD/YYYY</p>
          )}
        </div>

        <div className="flex justify-end gap-3">
          <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" onClick={handleSave} disabled={!date || saving}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>
    </div>
  );
}
